import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { getSubscription, getSubscriptionStatus, selectUser } from '../../../reducers/user.selectors';
import { AsyncActionStatus } from '../../../actions/common.actionTypes';
import { Box, Typography } from '@mui/material';
import MasterLayout from '../../../components/AppLayout/MasterLayout';
import FAQ from '../../../components/Pricing/FAQ';
import { customSizes, palette, spaces } from '../../../assets/styles/themes/tokens';
import useScreenSize from '../../../utils/useScreenSize';
import { selectActiveLicences } from '../../../reducers/licenceModels.selectors';
import PricingPackage from '../../../components/PricingPackage';
import { LicenceModel } from '@klab-berlin/api-sdk/lib/types/responses/Billing';
import useRouter from '../../../utils/useRouter';
import { 
  externalLinks, 
  meinUnterrichtSchulePackage, 
  routes, 
  userType, 
  packageLicenceIds, 
  ampliSubscriptionType, 
  AmpliPremiumPlanSelectedPayload,
  newPricingPackageLicenceIds
} from '../../../constants';
import { User } from '../../../types/User';
import { isNewPricingCurrentUserPlan, orderNewPricingPlans } from '../utils';
import services from '../../../services';
import { PremiumPlanSelected } from '../../../ampli';

const PricingPage: React.FC = () => {
  const { t } = useTranslation();
  const { router } = useRouter();
  const { isLgScreen, isMdScreen  } = useScreenSize();
  const isDesktopScreen = isLgScreen || isMdScreen;
  const user = useSelector(selectUser);
  const subscription = useSelector(getSubscription);
  const subscriptionStatus = useSelector(getSubscriptionStatus);
  const licenceModels = useSelector(state => selectActiveLicences(state));
  const showSchoolPackage = user?.type === userType.kombi || user?.type === userType.document;
  const { meinUnterrichtProForTrainees, meineFortbildung } = packageLicenceIds;

  useEffect(() => {
    if (user && user?.canUpgrade === false) {
      router.push(`/${routes.dashboard.root}`);
    }
  }, [user?.canUpgrade]);

  if (!subscriptionStatus || ![AsyncActionStatus.READY, AsyncActionStatus.NOTFOUND].includes(subscriptionStatus)){
    return <></>;
  }

  const isLicencesDataReady = () => !_.isEmpty(licenceModels);
  
  // Helper function to check if a licence is the user's current plan
  const isCurrentUserPlan = (licenceId: number) => {
    const userLicenceId =
      subscription?.licence?.licenceModelId ?
        Number(subscription.licence.licenceModelId) :
        undefined;
    return isNewPricingCurrentUserPlan(user?.type ?? 0, licenceId, userLicenceId);
  };

  // Determine which plans to show for new pricing
  const currentUserLicenceId = subscription?.licence?.licenceModelId 
    ? Number(subscription.licence.licenceModelId) 
    : undefined;

  let plans = Object.values(licenceModels)
    .filter(licence => {
      // Always include the user's current plan (even if it doesn't have allowedUserTypes)
      const isCurrentPlan = currentUserLicenceId && licence.licenceModelId === currentUserLicenceId;
      if (isCurrentPlan) {
        return true;
      }
      
      // For other plans, check allowedUserTypes
      return user?.type 
        && licence.allowedUserTypes?.includes(user.type) 
        && licence.licenceModelId !== meinUnterrichtProForTrainees;
    })
    .filter(licence => {
      // Always include the user's current plan (even if it has newPricing = false)
      const isCurrentPlan = currentUserLicenceId && licence.licenceModelId === currentUserLicenceId;
      
      if (isCurrentPlan) {
        return true;
      }
      
      // For basis users without subscription, show only new pricing plans + 1520 (courses)
      if (user?.type === 1 && !subscription?.licence?.licenceModelId) {
        return licence.newPricing === true || licence.licenceModelId === meineFortbildung;
      }
      
      // For other users, only show new pricing plans as upgrade options
      return licence.newPricing === true;
    });

  // Licence 3830 collect the same description data as 2320 licence.
  const proLicence = licenceModels[newPricingPackageLicenceIds.meinUnterrichtProKI];
  plans = plans.map(plan => {
    if (plan.licenceModelId === newPricingPackageLicenceIds.meinUnterrichtProTrial && proLicence) {
      return {
        ...plan,
        name: proLicence.name || plan.name,
        caption: proLicence.caption || 'Bibliothek + Tools + Fortbildungen',
        description: proLicence.description || plan.description,
        benefits: proLicence.benefits || plan.benefits,
      };
    }
    return plan;
  });

  // Order the plans using new pricing logic
  plans = orderNewPricingPlans(plans, user?.type, currentUserLicenceId);

  const sendPremiumPlanSelectedEvent = (licenceId: number, plan: LicenceModel) => {
    if (user) { 
      const payload: AmpliPremiumPlanSelectedPayload = {
        'license id': licenceId,
        'subscription interval': `${plan.period}mo`,
        'subscription type': licenceId === meinUnterrichtProForTrainees
          ? ampliSubscriptionType.REF : ampliSubscriptionType.TEACHER,
      };     
      services.track.ampliEventTrack({
        event: new PremiumPlanSelected(payload)
      });
    }
  };

  const getSecondaryCta = (plan: LicenceModel) => {
    if (plan.refDiscount && user?.type === userType.basis) {
      const { refDiscount, licenceModelId } = plan;
      const query: { licenceId?: string; promoCode?: string } = { };

      if (refDiscount.type === 'licence') {
        query.licenceId = refDiscount.value;
      } else if (refDiscount.type === 'promoCode') {
        query.licenceId = licenceModelId.toString();
        query.promoCode = refDiscount.value;
      }
      return {
        label: `50% ${t('Trainee Discount')}`,
        onClick: () => {
          sendPremiumPlanSelectedEvent(Number(query.licenceId), plan);
        },
        href: `/${routes.upgradePayment.root}?${new URLSearchParams(query).toString()}`,
      };
    }

    return undefined;
  };

  const getPackageInfo = (plan: LicenceModel, user: User) => {
    const caption = plan.caption;
    const packageName = plan.name;
    const description = t(plan.description || '');
    const isMostPopular = plan.isMostPopular;
    let price = `${plan.price.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € mtl.`;
    let duration = `${plan.period} ${t('month term')}`;
    let showBenefitsSummary = false;
    let benefits = plan.benefits || [];   
    
    const primaryCta = {
      label: undefined,
      onClick: () => {
        sendPremiumPlanSelectedEvent(plan.licenceModelId, plan);
      },
      href: `/${routes.upgradePayment.root}?licenceId=${plan.licenceModelId.toString()}`,
    };
  
    let secondaryCta = getSecondaryCta(plan);
  
    const isCurrentPlan = isCurrentUserPlan(plan.licenceModelId);
    
    // For upgrade option (2720), use the correct licence ID
    if (plan.licenceModelId === newPricingPackageLicenceIds.meinUnterricht360KI) {
      primaryCta.href = `/${routes.upgradePayment.root}?licenceId=${newPricingPackageLicenceIds.meinUnterricht360KI}`;
    }
    
    // If this is the user's current plan, don't show any CTA button
    if (isCurrentPlan) {
      primaryCta.label = undefined;
      primaryCta.onClick = () => {}; // Empty function
    }
    
    const packageHighlight = {
      enable: isMostPopular || isCurrentPlan,
      label: isMostPopular && user?.type !== userType.document ? t('Most popular') : t('Your current plan'),
      color: isMostPopular && user?.type !== userType.document ? 'success' as const : 'primary' as const,
      borderColor: isMostPopular && user?.type !== userType.document ? palette.success.main : palette.primary.main,
    };

    // Special pricing for document users upgrading to 2720
    if (user.type === userType.document && plan.licenceModelId === newPricingPackageLicenceIds.meinUnterricht360KI) {
      const hasPromoDiscount = !!subscription?.promoCode && subscription.promoCode === plan.refDiscount?.value;
      const newLicencePrice = licenceModels[newPricingPackageLicenceIds.meinUnterricht360KI].price;
      
      let newPrice: number;
      if (hasPromoDiscount) {
        const discountPercentage = subscription?.discounts?.[0]?.discountPercentage;
        const reducedPrice = subscription?.reducedPrice;

        if (!discountPercentage || !reducedPrice) {
          newPrice = 5;
        } else {
          const discountedLicencePrice = newLicencePrice * (1 - discountPercentage / 100);
          newPrice = Math.max(0, Math.round((discountedLicencePrice - reducedPrice) * 100) / 100);
        }
      } else if (!subscription?.licence?.pricePerMonth) {
        newPrice = 10;
      } else {
        // Case for LB users, that use custom prices fro the old plans
        newPrice = Math.max(0, newLicencePrice - subscription.licence.pricePerMonth);
      }

      price = `+${newPrice}€ mtl.`;
      duration = t('Until the end of your subscription');
      primaryCta.label = t('Switch now');
      primaryCta.href = `/${routes.upgradePayment.root}?licenceId=${newPricingPackageLicenceIds.meinUnterricht360KI}`;
      secondaryCta = undefined;
      showBenefitsSummary = true;
      benefits = plan.benefitsOverDocument || [];
    }

    return {
      packageName,
      description, 
      caption,
      isMostPopular,
      price, 
      duration, 
      packageHighlight, 
      primaryCta, 
      secondaryCta, 
      isLicenceIdCurrentUserPlan: isCurrentPlan,
      showBenefitsSummary, 
      benefits,
    };
  };
  
  return (
    <MasterLayout forceNewNavigation sidebarCollapsed>
      <Box 
        data-testid='pricing-page-container'
        sx={{
          backgroundColor: palette.common.white,
        }}
      >
        <Box
          data-testid='pricing-table-container'
        >
          <Box
            data-testid='pricing-title-container'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: spaces.md,
              paddingY: spaces.xxl,
            }}
          >
            <Typography variant='h5' sx={{ textAlign: 'center', color: palette.text.primary }}>
              {t('Find the right plan for your goals')}
            </Typography>
            <Typography variant='subtitle1' sx={{ textAlign: 'center', color: palette.text.secondary }}>
              {t('Tens of thousands of teachers and hundreds of schools already trust us')}
            </Typography>
          </Box>
          <Box 
            sx={{
              display: 'flex',
              flexDirection: isDesktopScreen ? 'row' : 'column',
              justifyContent: 'center',
              alignItems: isDesktopScreen ? 'normal' : 'center',
              gap: isDesktopScreen ? spaces.md : spaces.xl,
            }}
          >
            {isLicencesDataReady() && user && plans.map((plan, index) => (
              <PricingPackage
                key={index}
                {...getPackageInfo(plan, user)}
              />
            ))}
            {showSchoolPackage && (
              <PricingPackage
                packageName={meinUnterrichtSchulePackage.name}
                description={t(meinUnterrichtSchulePackage.description)}
                price={t(meinUnterrichtSchulePackage.price)}
                duration={t(meinUnterrichtSchulePackage.duration)}
                packageHighlight={{ enable: false }}
                isLicenceIdCurrentUserPlan={false}
                primaryCta={{ 
                  label: t('Request an offer now'),
                  href: externalLinks.schoolLicenceOffer
                }}
                benefits={meinUnterrichtSchulePackage.benefits}
                isMostPopular={false}
              />)}
          </Box>
        </Box>
        <Box
          data-testid='faq-container' 
          sx={{
            width: isDesktopScreen ? customSizes.pricing.faq.width.desktop : customSizes.pricing.faq.width.mobile,
            margin: '0 auto',
            paddingTop: spaces.xl,
            paddingBottom: spaces.xl,
          }}
        >
          <FAQ />
        </Box>
      </Box>
    </MasterLayout>
  );
};

export default PricingPage;
